import { Dispatch } from 'redux'

import type {
  TCreateDerivedMetricRequestBody,
  TDerivedMetricDto,
  TDerivedMetricPreviewRequestBody,
  TUpdateDerivedMetricRequestBody
} from '@homepal/api-contract'

import {
  create,
  destroy,
  getAll,
  getOptions,
  preview,
  update
} from 'redux/api/internal/DerivedMetrics'

import { useThunkActions } from 'redux/actions/useThunkActions'
import { parseApiError } from 'utils/errorParser'

import * as actions from './actions'

/**
 * Definitions are validated server side, and the message says exactly which
 * operand or slice is wrong — so it is shown in the form rather than
 * swallowed into a generic snackbar.
 *
 * `textsSynced` is false when the metric was saved but its texts did not
 * reach the CMS, which is a warning rather than a failure — and always the
 * case outside production.
 */
type SaveCallback = (
  error: string | null,
  metric: TDerivedMetricDto | null,
  textsSynced?: boolean
) => void

function parseError(error: unknown): string {
  const parsed = parseApiError(error as Parameters<typeof parseApiError>[0])

  return parsed.detail ?? 'Något gick fel'
}

export function tryGetDerivedMetrics() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

export function tryGetDerivedMetricOptions() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getOptions())

    getOptions()
      .then((response) => {
        dispatch(actions.getOptionsSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getOptionsFailed())
      })
  }
}

export function tryPreviewDerivedMetric(
  data: TDerivedMetricPreviewRequestBody,
  callback: (error: string | null) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.preview())

    preview({ data })
      .then((response) => {
        dispatch(actions.previewSuccess(response.data.data))

        callback(null)
      })
      .catch((e) => {
        dispatch(actions.previewFailed())

        callback(parseError(e))
      })
  }
}

export function tryCreateDerivedMetric(
  data: TCreateDerivedMetricRequestBody,
  callback: SaveCallback
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create({ data })
      .then((response) => {
        const { metric, texts_synced: textsSynced } = response.data.data

        dispatch(actions.createSuccess(metric))

        callback(null, metric, textsSynced)
      })
      .catch((e) => {
        dispatch(actions.createFailed())

        callback(parseError(e), null)
      })
  }
}

export function tryUpdateDerivedMetric(
  id: string,
  data: TUpdateDerivedMetricRequestBody,
  callback: SaveCallback
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, { data })
      .then((response) => {
        const { metric, texts_synced: textsSynced } = response.data.data

        dispatch(actions.updateSuccess(metric))

        callback(null, metric, textsSynced)
      })
      .catch((e) => {
        dispatch(actions.updateFailed())

        callback(parseError(e), null)
      })
  }
}

export function tryDeleteDerivedMetric(
  id: string,
  callback: (error: string | null) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))

        callback(null)
      })
      .catch((e) => {
        dispatch(actions.destroyFailed())

        callback(parseError(e))
      })
  }
}

export const useDerivedMetricActions = () => {
  return {
    tryGetDerivedMetrics: useThunkActions(tryGetDerivedMetrics),
    tryGetDerivedMetricOptions: useThunkActions(tryGetDerivedMetricOptions),
    tryPreviewDerivedMetric: useThunkActions(tryPreviewDerivedMetric),
    tryCreateDerivedMetric: useThunkActions(tryCreateDerivedMetric),
    tryUpdateDerivedMetric: useThunkActions(tryUpdateDerivedMetric),
    tryDeleteDerivedMetric: useThunkActions(tryDeleteDerivedMetric)
  }
}
